/**
 * Auth context — provides user state, login/logout, role checks.
 *
 * Usage:
 *   import { useAuth } from '../context/AuthContext';
 *   const { user, isAdmin, isMember, login, logout } = useAuth();
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const token = api.getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      const data = await api.get('/app/api/user/profile');
      if (data) {
        setUser(data);
      } else {
        api.clearToken();
        setUser(null);
      }
    } catch {
      api.clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const login = async (token) => {
    api.setToken(token);
    try {
      const profile = await api.get('/app/api/user/profile');
      setUser(profile);
      return profile;
    } catch {
      api.clearToken();
      return null;
    }
  };

  const logout = () => {
    api.clearToken();
    setUser(null);
  };

  const isAdmin = user?.workspace_role === 'full_admin';
  const isBillingAdmin = user?.workspace_role === 'billing_admin';
  const isMember = !!user?.is_workspace_member && !isAdmin && !isBillingAdmin;
  const isWorkspaceMember = !!user?.is_workspace_member;

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, login, logout, loadProfile,
      isAdmin, isBillingAdmin, isMember, isWorkspaceMember,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
